import {
    cloneElement,
    type ReactElement,
    type ReactNode,
    type JSX,
} from 'react';
import { type VariantProps, tv } from 'tailwind-variants';
import {
    default as ReactCountdown,
    type CountdownRenderProps,
} from 'react-countdown';

import type { CommonProps } from '../../types';
import { TFunction, useTranslation } from 'next-i18next';
import { Text } from '../../atoms';

export interface CountdownProps
    extends CommonProps,
        VariantProps<typeof CountdownStyles> {
    date: string | number | Date;
    button?: ReactElement<{ className?: string }>;
    title?: string | null;
    completedText?: string;
}

export const CountdownStyles = tv({
    slots: {
        root: 'bg-gray-500 rounded-xl p-5 mb-4 lg:mb-8 text-center',
        countdownContainer:
            'grid auto-cols-fr items-start grid-flow-col gap-3  md:auto-cols-min justify-center',
        countdownCircle:
            'relative aspect-square p-1 rounded-full bg-buttonPrimary text-buttonPrimary max-sm:text-2xs leading-none md:w-[100px]',
        countdownContent:
            'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col',
        countdownButton:
            'border-2 border-dashed bg-transparent border-white text-white  flex items-center justify-center',
        title: 'font-bold mb-3 block text-sm sm:text-lg',
        countdownNumber: 'text-lg leading-none sm:text-2xl',
    },
});

const CountdownCircle = ({
    classOverwrite,
    children,
}: {
    classOverwrite?: string;
    children: ReactNode;
}) => {
    const { countdownCircle, countdownContent } = CountdownStyles();
    return (
        <div className={countdownCircle({ className: classOverwrite })}>
            <div className={countdownContent()}>{children}</div>
        </div>
    );
};

const renderer = (
    { days, hours, minutes, seconds, completed }: CountdownRenderProps,
    props: {
        button?: ReactElement<{ className?: string }>;
        t: TFunction;
        title?: string | null;
        completedText?: string;
    }
) => {
    const {
        countdownContainer,
        countdownButton,
        countdownCircle,
        countdownNumber,
        title: titleStyle,
    } = CountdownStyles();

    const clonedButton = () => {
        if (props.button) {
            return cloneElement(props.button, {
                ...props.button.props,
                className: countdownCircle({
                    className: countdownButton({
                        className: props.button.props.className,
                    }),
                }),
            });
        }
        return;
    };
    if (!completed) {
        return (
            <div>
                {props.title && (
                    <Text className={titleStyle()}>{props.title}</Text>
                )}

                <div className={countdownContainer()}>
                    <CountdownCircle>
                        <span className={countdownNumber()}>
                            {('00' + days).slice(-2)}
                        </span>

                        {props.t(
                            days != 1 ? 'countdown.days' : 'countdown.day'
                        )}
                    </CountdownCircle>
                    <CountdownCircle>
                        <span className={countdownNumber()}>
                            {('00' + hours).slice(-2)}
                        </span>
                        {props.t(
                            hours != 1 ? 'countdown.hours' : 'countdown.hour'
                        )}
                    </CountdownCircle>
                    <CountdownCircle>
                        <span className={countdownNumber()}>
                            {('00' + minutes).slice(-2)}
                        </span>
                        {props.t(
                            minutes != 1
                                ? 'countdown.minutes'
                                : 'countdown.minute'
                        )}
                    </CountdownCircle>
                    <CountdownCircle>
                        <span className={countdownNumber()}>
                            {('00' + seconds).slice(-2)}
                        </span>
                        {props.t(
                            seconds != 1
                                ? 'countdown.seconds'
                                : 'countdown.second'
                        )}
                    </CountdownCircle>
                    {props.button && clonedButton()}
                </div>
            </div>
        );
    }
    if (props.completedText) {
        return <Text className={titleStyle()}>{props.completedText}</Text>;
    }
    return <></>;
};

export const Countdown = ({
    className,
    date,
    testId = 'countdown',
    button,
    title,
    completedText,
    ...props
}: CountdownProps): JSX.Element => {
    const { root } = CountdownStyles();
    const { t } = useTranslation(['event']);

    return (
        <div className={root({ className })} {...props} data-testid={testId}>
            <ReactCountdown
                date={date}
                renderer={(props) =>
                    renderer(props, { t, button: button, title, completedText })
                }
            />
        </div>
    );
};
